import { FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  DataGridCell,
  DataGridDateCell,
  getEntity,
} from "@components/common/data-grid/adm-data-grid-utils";
import { formatGroupSqftAssigned } from "@utils/data-display";
import { ReactNode } from "react";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  GeneralTableEvents,
  WorkspaceEvents,
} from "@utils/track-event/track-event-list";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { DataGridOptionCell } from "@components/common/data-grid/data-grid-option-cell";
import { useGenerateRoutes } from "@router/router-helper";
import { useToast } from "@flat-ui";

/** Return group name cell in data-grid */
export function GroupNameCell(
  params: EntityCellParams<FaroCoreApiTypes.AdmCompanyGroup>
): ReactNode {
  const entity = getEntity(params);
  return (
    <DataGridCell
      content={entity.name}
      tooltip={`${entity.name} (${entity.id})`}
    />
  );
}

/** Return group managerCount cell in data-grid */
export function GroupManagerCountCell(
  params: EntityCellParams<FaroCoreApiTypes.AdmCompanyGroup>
): ReactNode {
  return <DataGridCell content={getEntity(params).managerCount} />;
}

/** Return group projectCount cell in data-grid */
export function GroupProjectCountCell(
  params: EntityCellParams<FaroCoreApiTypes.AdmCompanyGroup>
): ReactNode {
  return <DataGridCell content={getEntity(params).projectCount} />;
}

/** Return group sqft used cell in data-grid */
export function GroupSqftUsedCell(
  params: EntityCellParams<FaroCoreApiTypes.AdmCompanyGroup>
): ReactNode {
  return <DataGridCell content={getEntity(params).sqft.used} />;
}

/** Return group sqft assigned cell in data-grid */
export function GroupSqftAssignedCell(
  params: EntityCellParams<FaroCoreApiTypes.AdmCompanyGroup>
): ReactNode {
  return (
    <DataGridCell
      content={formatGroupSqftAssigned(getEntity(params).sqft.assigned)}
    />
  );
}

/** Return group created at cell in data-grid */
export function GroupCreatedAtCell(
  params: EntityCellParams<FaroCoreApiTypes.AdmCompanyGroup>
): ReactNode {
  return <DataGridDateCell date={getEntity(params).createdAt} />;
}

/** Return group option cell in data-grid */
export function GroupOptionsCell(
  params: EntityCellParams<FaroCoreApiTypes.AdmCompanyGroup>,
  companyId: string
): ReactNode {
  const { trackEvent } = useTrackEvent();
  const { generateCompanyAddProjectPlanRoute } = useGenerateRoutes();
  const entity = getEntity(params);
  const { openToast } = useToast();

  return (
    <DataGridOptionCell
      item={entity}
      menuItems={[
        {
          label: "Copy group ID",
          onSelect: async () => {
            trackEvent({
              name: GeneralTableEvents.copyRowIdToClipboard,
              props: {
                location: "workspace-groups-table",
              },
            });
            await navigator.clipboard.writeText(entity.id);
            openToast({
              title: "Group ID copied to clipboard!",
            });
          },
        },
        {
          label: "Create project",
          permission: "workspace.createProject",
          onSelect: () => {
            trackEvent({
              name: WorkspaceEvents.navigateToCreateProject,
              props: {
                location: "workspace-groups-table",
              },
            });
          },
          getHrefUrl: (entity) =>
            generateCompanyAddProjectPlanRoute({
              companyId,
              groupId: entity.id,
            }).pathname,
        },
      ]}
    />
  );
}
